import React, { useState } from "react";
import moment from "moment";
import {
  Stack,
  IconButton,
  Icon,
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableSortLabel,
  TableBody,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import styled from "@emotion/styled";
import MDAvatar from "components/MDAvatar";
import sampleProfileImage from "assets/images/sample.png";
import { visuallyHidden } from "@mui/utils";
import MDInput from "components/MDInput";

const TableHeader = styled(TableHead)({
  [`& .MuiTableCell-root p`]: {
    fontSize: "0.9rem",
    fontWeight: "600",
    lineHeight: "1rem",
    color: "dark",
  },
  [`& .MuiTableCell-root`]: {
    padding: "0.75rem 0.5rem",
  },
});

const TableRowSlim = styled(TableRow)({
  [`& .MuiTableCell-root`]: {
    fontSize: "1rem",
    lineHeight: "1.2rem",
    padding: "0.75rem 0.5rem",
  },
});

function descendingComparator(a, b, orderBy) {
  let valueA = a?.UserCompaniesLink?.[orderBy] || a[orderBy];
  let valueB = b?.UserCompaniesLink?.[orderBy] || b[orderBy];

  if (!valueA) return 1;
  if (!valueB) return -1;

  if (["days"].includes(orderBy)) {
    return moment(valueA?.[0]?.day).isBefore(moment(valueB?.[0]?.day)) ? 1 : -1;
  }

  valueA = valueA?.toLowerCase();
  valueB = valueB?.toLowerCase();
  if (valueB < valueA) {
    return -1;
  }
  if (valueB > valueA) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const SortableLabel = React.memo(
  ({ labelKey, children, tableFilters, handleRequestSort }) => (
    <TableSortLabel
      active={tableFilters.orderBy === labelKey}
      direction={tableFilters.orderBy === labelKey ? tableFilters.order : "asc"}
      onClick={() => handleRequestSort(labelKey)}
      sx={{ display: "flex", justifyContent: "center" }}
    >
      {children}
      {tableFilters.orderBy === labelKey ? (
        <MDBox component="span" sx={visuallyHidden}>
          {tableFilters.order === "desc" ? "sorted descending" : "sorted ascending"}
        </MDBox>
      ) : null}
    </TableSortLabel>
  ),
  (prevProps, nextProps) =>
    prevProps.tableFilters.order === nextProps.tableFilters.order &&
    prevProps.tableFilters.orderBy === nextProps.tableFilters.orderBy &&
    prevProps.handleRequestSort === nextProps.handleRequestSort
);

const CompanyHolidaysUserTable = ({ companyHolidaysList = [], deleteCompanyHolidays }) => {
  const [tableFilters, setTableFilters] = useState({
    order: "asc",
    orderBy: "days",
    userSearchTerm: "",
  });

  const handleRequestSort = (property) => {
    const isAsc = tableFilters.orderBy === property && tableFilters.order === "asc";
    setTableFilters({
      order: isAsc ? "desc" : "asc",
      orderBy: property,
      userSearchTerm: tableFilters?.userSearchTerm,
    });
  };

  // const SortableLabel = React.memo(
  //   ({ labelKey, children }) => (
  //     <TableSortLabel
  //       active={tableFilters.orderBy === labelKey}
  //       direction={tableFilters.orderBy === labelKey ? tableFilters.order : "asc"}
  //       onClick={() => handleRequestSort(labelKey)}
  //       sx={{ display: "flex", justifyContent: "center" }}
  //     >
  //       {children}
  //       {tableFilters.orderBy === labelKey ? (
  //         <MDBox component="span" sx={visuallyHidden}>
  //           {tableFilters.order === "desc" ? "sorted descending" : "sorted ascending"}
  //         </MDBox>
  //       ) : null}
  //     </TableSortLabel>
  //   ),
  //   [tableFilters.order, tableFilters.orderBy, handleRequestSort]
  // );

  const handleSearchFilter = (row) => {
    if (!tableFilters.userSearchTerm) {
      return true;
    }
    return row?.UserCompaniesLink?.name
      ?.toLowerCase()
      .includes(tableFilters.userSearchTerm.toLowerCase());
  };

  const visibleRows = React.useMemo(() => {
    let allDataRecord = [];
    const filteredData = companyHolidaysList?.filter(handleSearchFilter);
    const findAllDataIndex = filteredData?.findIndex((obj) => obj.id === "all");
    if (findAllDataIndex > -1) allDataRecord = filteredData?.splice(findAllDataIndex, 1);
    return [
      ...allDataRecord,
      ...filteredData.sort(getComparator(tableFilters.order, tableFilters.orderBy)),
    ];
  }, [tableFilters, handleSearchFilter, companyHolidaysList]);

  const getConvertedDateString = (days = []) =>
    `${moment(days[0].day).format("dddd, MMMM Do")}${
      days?.length > 1 ? ` - ${moment(days[days.length - 1].day).format("dddd, MMMM Do")}` : ""
    }`;

  return (
    <>
      <MDInput
        mt={2}
        value={tableFilters?.userSearchTerm}
        onChange={(event) =>
          setTableFilters({
            ...tableFilters,
            userSearchTerm: event.target.value || "",
          })
        }
        label="Search employee"
        sx={{
          m: 2,
          mt: 4,
          // maxWidth: "40%",
          "& .MuiInputBase-root": {
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
            border: "1px solid rgba(0, 0, 0, 0.15)",
          },
        }}
      />
      <TableContainer component={Paper} sx={{ borderRadius: "0.5rem" }}>
        <Table aria-label="simple table">
          <TableHeader key="leader-board-table" sx={{ display: "table-header-group" }}>
            <TableRow>
              <TableCell width="40%" key="name">
                <SortableLabel
                  labelKey="name"
                  tableFilters={tableFilters}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Name</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell width="50%" key="days">
                <SortableLabel
                  labelKey="days"
                  tableFilters={tableFilters}
                  handleRequestSort={handleRequestSort}
                >
                  <MDTypography>Date</MDTypography>
                </SortableLabel>
              </TableCell>
              <TableCell key="actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows?.map((row, index) => (
              <TableRowSlim
                key={row?.id || index}
                sx={{
                  "& :hover": {
                    cursor: "pointer",
                  },
                  background: index % 2 ? "#fff" : "#f5f5f5",
                }}
              >
                <TableCell key={`name-${row.key}`}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <MDAvatar
                      variant="circular"
                      src={row?.UserCompaniesLink?.profile_picture || sampleProfileImage}
                    />
                    <MDBox mr={2} sx={{ color: "inherit" }}>
                      {row?.UserCompaniesLink?.name || row.name}
                    </MDBox>
                  </Stack>
                </TableCell>
                <TableCell align="center" key={`days-${row?.key}`}>
                  {getConvertedDateString(row?.days)}
                </TableCell>
                <TableCell align="center" key={`actions-${row?.key}`}>
                  {row?.isCustom ? (
                    <IconButton
                      size="large"
                      color="inherit"
                      variant="contained"
                      onClick={() => deleteCompanyHolidays(row)}
                    >
                      <Icon fontSize="medium">delete</Icon>
                    </IconButton>
                  ) : null}
                </TableCell>
              </TableRowSlim>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default CompanyHolidaysUserTable;
